// components/Services.js
import React from 'react';
import { motion } from 'framer-motion';
import { Code, Globe, ShoppingCart, Film, Palette, BarChart, Layout, Server, Smartphone, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Code />,
      title: 'Web Development',
      description: 'Custom web applications built with modern technologies for optimal performance.',
      color: 'blue'
    },
    {
      icon: <Globe />,
      title: 'WordPress Development',
      description: 'Professional WordPress websites with custom themes and plugins.',
      color: 'green'
    },
    {
      icon: <Layout />,
      title: 'Frontend Development',
      description: 'Responsive and interactive user interfaces using React, Vue, and Angular.',
      color: 'purple'
    },
    {
      icon: <Server />,
      title: 'Backend Development',
      description: 'Robust server-side solutions with Node.js, Python, and PHP frameworks.',
      color: 'red'
    },
    {
      icon: <ShoppingCart />,
      title: 'E-commerce',
      description: 'Complete online store solutions with secure payment integrations.',
      color: 'yellow'
    },
    {
      icon: <BarChart />,
      title: 'Digital Marketing',
      description: 'Comprehensive marketing strategies to boost your online presence.',
      color: 'pink'
    },
    {
      icon: <Film />,
      title: 'Video Editing',
      description: 'Professional video editing services for marketing and promotional content.',
      color: 'indigo'
    },
    {
      icon: <Palette />,
      title: 'Graphic Designing',
      description: 'Creative design solutions for branding and visual communication.',
      color: 'orange'
    },
    {
      icon: <Smartphone />,
      title: 'Full Stack Development',
      description: 'End-to-end web solutions covering both frontend and backend development.',
      color: 'teal'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    red: 'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    pink: 'bg-pink-100 text-pink-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    orange: 'bg-orange-100 text-orange-600',
    teal: 'bg-teal-100 text-teal-600'
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Services</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We offer a comprehensive range of technology services to help your business 
            thrive in the digital world.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
            >
              <div className={`w-12 h-12 rounded-lg ${colorClasses[service.color]} flex items-center justify-center mb-4`}>
                <div className="w-6 h-6">
                  {service.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {service.title}
              </h3>
              <p className="text-gray-600">
                {service.description}
              </p>
              <motion.button
                whileHover={{ x: 5 }}
                className="mt-4 text-blue-600 font-medium flex items-center"
              >
                Learn More
                <ArrowRight className="ml-1" size={16} />
              </motion.button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;